import type { ReactNode } from 'react';
import './source-css/theme.css';
import './source-css/app.css';
// Loaded AFTER app.css so it can override it. Squares off every chip and badge in APR
// and gives the ones carrying a status or a disposition the outline-and-wash treatment.
// Here rather than per-screen so it reaches screens nobody thought to open.
import './source-css/chips.css';

/**
 * The wrapper every screen cloned from APR renders inside.
 *
 * WHY THIS EXISTS
 * ---------------
 * APR's screens lean on its global stylesheet — `.page`, `.btn`, `.btn-primary`,
 * `.card`, `.modal`, `.rd-table`, `.empty-state`, `.form-*`. Application Portfolio Q&A
 * uses 42 such classes, APR Model 32. Cloned without that CSS a screen renders
 * unstyled; cloned WITH it, 10,935 lines of unscoped rules would land on the platform's
 * own screens and on MUI — `.btn`, `.card` and `.page` are exactly the names that break
 * things.
 *
 * So the source's `App.css` and `styles/theme.css` are copied into `source-css/` with
 * every selector mechanically prefixed to `.apr-app`, and every cloned screen renders
 * inside this wrapper. The rules reach APR's screens and cannot reach anything else.
 * `:root` became `.apr-app`, so the source's custom properties resolve here and nowhere
 * else; `@keyframes` and `@font-face` were left alone.
 *
 * The pay-off: a cloned screen keeps the source's markup and class names verbatim, so
 * porting is copy-and-fix-imports rather than translate-and-measure. It is the same
 * technique the source already used for its own `StartPage.css`, which is scoped under
 * `.apr-start`.
 *
 * REGENERATING
 * ------------
 * The source is frozen, so these files should not need regenerating. If they ever do:
 *
 *   python3 /tmp/scope_css.py <source>/src/App.css        source-css/app.css
 *   python3 /tmp/scope_css.py <source>/src/styles/theme.css source-css/theme.css
 *
 * (the script is reproduced in docs/CONVENTIONS.md — it prefixes top-level selectors,
 * recurses into @media, and skips @keyframes/@font-face bodies.)
 *
 * DO NOT hand-edit `source-css/`. A difference there is a difference from the source,
 * and the whole point is that there are none.
 */
export function AprScreen({ children }: { children: ReactNode }) {
  return <div className="apr-app">{children}</div>;
}
